import React, { useCallback, useEffect, useState } from "react";
import { AppSettings, getAppSettings, updateAppSettings } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import { can } from "../../utils/roles";
import { FaLock, FaSyncAlt } from "react-icons/fa";

/**
 * The numbers the business owns.
 *
 * Deliberately narrow: this is not a control panel for the product, it is the
 * handful of values somebody should be able to change at four o'clock on a
 * Friday without a deploy. Anything that changes what the system *does* stays
 * in code, where it gets reviewed.
 */

const errorOf = (e: any, fallback: string) =>
    e?.response?.data?.errors?.[0]?.message ?? e?.response?.data?.error ?? fallback;

const when = (iso: string | null) =>
    iso
        ? new Date(iso).toLocaleString(undefined, {
            day: "numeric", month: "short", year: "numeric",
            hour: "2-digit", minute: "2-digit",
        })
        : null;

interface Bounds { min: number; max: number }

/**
 * One editable number, with the bounds it will be held to said before it is
 * refused rather than after.
 *
 * The field keeps its value as a string so it can be empty mid-retype — a
 * number-typed state would turn an empty box into 0 and save it.
 */
const NumberSetting: React.FC<{
    label: string;
    value: string;
    onChange: (next: string) => void;
    bounds: Bounds;
    unit: string;
    editable: boolean;
    saving: boolean;
    dirty: boolean;
    onSave: () => void;
    hint: string;
}> = ({ label, value, onChange, bounds, unit, editable, saving, dirty, onSave, hint }) => {
    const parsed = Number(value);
    const valid =
        value.trim() !== "" &&
        Number.isInteger(parsed) &&
        parsed >= bounds.min &&
        parsed <= bounds.max;

    return (
        <>
            <div className="mt-5 flex flex-wrap items-end gap-3">
                <label className="block">
                    <span className="block text-sm font-medium text-gray-700 mb-1">{label}</span>
                    <input
                        type="number"
                        inputMode="numeric"
                        min={bounds.min}
                        max={bounds.max}
                        value={value}
                        disabled={!editable || saving}
                        onChange={(e) => onChange(e.target.value)}
                        className="w-32 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-gray-50 disabled:text-gray-500"
                    />
                </label>

                {editable && (
                    <button
                        onClick={onSave}
                        disabled={saving || !dirty || !valid}
                        className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-gray-800 disabled:opacity-50"
                    >
                        {saving ? "Saving…" : "Save"}
                    </button>
                )}
            </div>

            <p className="mt-2 text-xs text-gray-400">
                Between {bounds.min} and {bounds.max} {unit}. Whole numbers only. {hint}
            </p>

            {!valid && value.trim() !== "" && (
                <p className="mt-2 text-xs text-red-600">
                    {Number.isInteger(parsed)
                        ? `Must be between ${bounds.min} and ${bounds.max}.`
                        : "Whole numbers only."}
                </p>
            )}
        </>
    );
};

const Settings: React.FC = () => {
    const { profile } = useAuth();

    const [settings, setSettings] = useState<AppSettings | null>(null);
    const [windowBounds, setWindowBounds] = useState<Bounds>({ min: 1, max: 1440 });
    const [discountBounds, setDiscountBounds] = useState<Bounds>({ min: 0, max: 100 });
    /** Kept as strings so the fields can be empty while being retyped. */
    const [windowMinutes, setWindowMinutes] = useState("");
    const [discountPercent, setDiscountPercent] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [saved, setSaved] = useState("");

    const allowed = can(profile, "settings");
    const editable = can(profile, "settings", "manage");

    const load = useCallback(async () => {
        if (!allowed) return;
        setLoading(true);
        try {
            const page = await getAppSettings();
            setSettings(page.settings);
            setWindowBounds(page.limits.paymentWindowMinutes);
            setDiscountBounds(page.limits.maxDiscountPercentOfFee);
            setWindowMinutes(String(page.settings.paymentWindowMinutes));
            setDiscountPercent(String(page.settings.maxDiscountPercentOfFee));
            setError("");
        } catch (e: any) {
            setError(errorOf(e, "Could not load the settings."));
        } finally {
            setLoading(false);
        }
    }, [allowed]);

    useEffect(() => { load(); }, [load]);

    /**
     * Save one field.
     *
     * Only the field that changed is sent. The server treats anything absent as
     * "leave it alone", so editing the payment window cannot quietly overwrite
     * the discount policy with whatever this screen last happened to read.
     */
    const save = async (patch: Parameters<typeof updateAppSettings>[0], done: string) => {
        setSaving(true);
        setError("");
        setSaved("");
        try {
            const next = await updateAppSettings(patch);
            setSettings(next);
            setWindowMinutes(String(next.paymentWindowMinutes));
            setDiscountPercent(String(next.maxDiscountPercentOfFee));
            setSaved(done);
        } catch (e: any) {
            // The server explains what it will accept — show that rather than
            // a generic failure, because the bounds are the whole message.
            setError(errorOf(e, "That could not be saved."));
        } finally {
            setSaving(false);
        }
    };

    if (!allowed) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center px-4">
                <div className="max-w-sm text-center">
                    <FaLock className="mx-auto text-gray-400 mb-3" size={30} />
                    <h1 className="text-lg font-semibold text-gray-900 mb-1">Not available</h1>
                    <p className="text-sm text-gray-500">
                        Only head office can change these settings.
                    </p>
                </div>
            </div>
        );
    }

    const windowDirty =
        settings !== null && windowMinutes !== String(settings.paymentWindowMinutes);
    const discountDirty =
        settings !== null && discountPercent !== String(settings.maxDiscountPercentOfFee);

    const lastChanged = settings?.updatedBy ? (
        <p className="mt-4 border-t border-gray-100 pt-3 text-xs text-gray-400">
            Last changed by {settings.updatedBy}
            {when(settings.updatedAt) ? ` on ${when(settings.updatedAt)}` : ""}.
        </p>
    ) : null;

    const readOnlyNote = !editable ? (
        <p className="mt-4 text-xs text-gray-500">You can see this but not change it.</p>
    ) : null;

    return (
        <div className="max-w-3xl mx-auto px-4 py-8">
            <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-900">Settings</h1>
                    <p className="text-sm text-gray-500 mt-1">
                        How the system behaves. Changes take effect without a restart.
                    </p>
                </div>
                <button
                    onClick={load}
                    disabled={loading}
                    className="flex items-center gap-2 text-sm border border-gray-300 rounded-lg px-3 py-2 hover:bg-gray-50 disabled:opacity-50"
                >
                    <FaSyncAlt className={loading ? "animate-spin" : ""} /> Refresh
                </button>
            </div>

            {error && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {error}
                </div>
            )}
            {saved && (
                <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
                    {saved}
                </div>
            )}

            {loading && !settings ? (
                <div className="rounded-xl border border-gray-200 bg-white px-4 py-10 text-center text-gray-400">
                    Loading…
                </div>
            ) : (
                <div className="space-y-6">
                    <div className="rounded-xl border border-gray-200 bg-white p-6">
                        <h2 className="text-base font-semibold text-gray-900">
                            Hold on unpaid bookings
                        </h2>
                        <p className="mt-1 text-sm text-gray-500">
                            A booking that has not been paid for within this time is expired
                            automatically. Nothing is held at the airline before payment, so an
                            expired booking costs nobody a seat — it stops the list filling with
                            sales that were never going to happen.
                        </p>

                        <NumberSetting
                            label="Minutes"
                            value={windowMinutes}
                            onChange={(v) => { setWindowMinutes(v); setSaved(""); }}
                            bounds={windowBounds}
                            unit="minutes"
                            editable={editable}
                            saving={saving}
                            dirty={windowDirty}
                            onSave={() => save(
                                { paymentWindowMinutes: Number(windowMinutes) },
                                "Saved. It applies from the next sweep, within a minute.",
                            )}
                            hint=""
                        />

                        {lastChanged}
                        {readOnlyNote}
                    </div>

                    <div className="rounded-xl border border-gray-200 bg-white p-6">
                        <h2 className="text-base font-semibold text-gray-900">
                            How much staff may take off a fare
                        </h2>
                        <p className="mt-1 text-sm text-gray-500">
                            A discount comes out of the agency's own service fee on that fare and
                            never out of what the airline is owed — so the fee is a hard ceiling
                            whatever this is set to. This decides how much of the fee an agent may
                            actually give away.
                        </p>
                        <p className="mt-2 text-sm text-gray-500">
                            <b>100</b> lets them give away the whole fee, earning nothing on the
                            sale. <b>0</b> switches discounts off — the box disappears from the
                            payment screen.
                        </p>

                        <NumberSetting
                            label="Percent of the fee"
                            value={discountPercent}
                            onChange={(v) => { setDiscountPercent(v); setSaved(""); }}
                            bounds={discountBounds}
                            unit="percent"
                            editable={editable}
                            saving={saving}
                            dirty={discountDirty}
                            onSave={() => save(
                                { maxDiscountPercentOfFee: Number(discountPercent) },
                                "Saved. It applies to the next sale, not to bookings already made.",
                            )}
                            hint="It never lets a sale go below the airline's fare."
                        />

                        {lastChanged}
                        {readOnlyNote}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Settings;
